import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import { toast } from 'react-toastify';
import { Alert, Button, Card, Input, Popconfirm, Radio } from 'antd';
import { CheckCircleFilled, CloseCircleFilled, CopyOutlined, InfoCircleOutlined } from '@ant-design/icons';
import useApiClient from '../../hooks/api/useApiClient';
import { actionCreators } from '../../store/actions';
import { DeliveryMode } from '../../services/exports/Constants';
import { firstApiError } from '../../services/helpers/ApiErrors';
import {
  WoltDriveProviderPriority,
  formatDeliveryProviderChain,
  isValidWoltVenueId,
  maskWoltVenueId,
  normaliseWoltVenueId,
} from '../../services/helpers/WoltDriveOnboarding';

/**
 * Wolt Drive onboarding for the current restaurant (PROD-491): copy its row for
 * the sheet shared with Wolt, then verify the venue ID Wolt sends back and put
 * Wolt first in the provider order. Rendered by DeliveryTab for super admins and
 * the platform's onboarding managers, and only until the restaurant has a valid
 * venue ID: a successful save stores it, and the card goes away with it.
 */
export default function WoltDriveOnboardingCard({ hasUnsavedProviderChanges = false, onDeliveryProvidersChange }) {
  const { t } = useTranslation(undefined, { keyPrefix: 'Components:Takeout:WoltDriveOnboardingCard' });

  const dispatch = useDispatch();
  const { company } = useSelector((state) => state.currentCompany);
  const { WoltDriveOnboardingManager } = useApiClient();

  // Fetched ahead of the click: a clipboard write after an awaited request can
  // lose the browser's user-activation window (Safari) and be refused.
  const [sheetRow, setSheetRow] = useState(null);
  const [copying, setCopying] = useState(false);

  const [venueIdInput, setVenueIdInput] = useState('');
  const [verifying, setVerifying] = useState(false);
  const [verification, setVerification] = useState(null);
  const [verificationError, setVerificationError] = useState(null);
  const [priority, setPriority] = useState(WoltDriveProviderPriority.WoltPrimary);
  const [saving, setSaving] = useState(false);

  const candidate = normaliseWoltVenueId(venueIdInput);
  const candidateIsValid = isValidWoltVenueId(candidate);
  const showFormatError = venueIdInput.trim() !== '' && !candidateIsValid;
  // A result belongs to the ID it was given for; editing the field retires it.
  const verified = verification?.venueId === candidate ? verification : null;
  const failure = verificationError?.venueId === candidate ? verificationError : null;

  const loadSheetRow = async () => {
    const { success, data } = await WoltDriveOnboardingManager.getSheetRow();

    if (!success) {
      return { error: firstApiError(data) };
    }

    setSheetRow(data.data);

    return { row: data.data };
  };

  // Once per restaurant: DeliveryTab keys the card by company, so a switch remounts it.
  useEffect(() => {
    loadSheetRow();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const copyDetails = async () => {
    let row = sheetRow;

    if (!row) {
      setCopying(true);
      const result = await loadSheetRow();
      setCopying(false);

      if (!result.row) {
        toast.error(result.error ?? t('toasts.copy_failed'));

        return;
      }

      row = result.row;
    }

    try {
      await navigator.clipboard.writeText(row.tsv);
      toast.success(t('toasts.copied'));
    } catch (error) {
      console.error('Wolt Drive onboarding: copying the sheet row failed', error);
      toast.error(t('toasts.copy_failed'));
    }
  };

  const verify = async () => {
    setVerifying(true);
    const { success, data } = await WoltDriveOnboardingManager.verify(candidate);
    setVerifying(false);

    if (!success) {
      setVerification(null);
      setVerificationError({
        venueId: candidate,
        message: firstApiError(data),
        reason: data?.payload?.reason ?? null,
      });

      return;
    }

    setVerificationError(null);
    setVerification({ venueId: candidate, ...data.data });
    setPriority(WoltDriveProviderPriority.WoltPrimary);
  };

  const save = async () => {
    setSaving(true);
    const { success, data } = await WoltDriveOnboardingManager.activate({
      woltDriveVenueId: verified.venueId,
      providerPriority: priority,
    });
    setSaving(false);

    if (!success) {
      // Saving verifies with Wolt again, so the venue can fail here too.
      setVerification(null);
      setVerificationError({
        venueId: candidate,
        message: firstApiError(data),
        reason: data?.payload?.reason ?? null,
      });
      toast.error(t('toasts.save_failed'));

      return;
    }

    toast.success(t('toasts.saved'));
    onDeliveryProvidersChange(data.data.delivery_providers);
    // Last: the stored venue ID is what takes this card off the Delivery tab.
    dispatch(actionCreators.currentCompany.update(data.data));
  };

  const plannedProviders = verified?.delivery_providers_by_priority?.[priority] ?? [];

  const renderVerificationResult = () => {
    if (verified) {
      return (
        <>
          <div className="flex items-center gap-2 mt-sm text-green-600">
            <CheckCircleFilled />
            <span>{t('verification.verified')}</span>
          </div>
          {/* Wolt knows the venue but would not quote it right now (closed, outside its hours). */}
          {verified.unavailable_reason && (
            <div className="flex items-center gap-2 mt-xs text-sm text-gray-500">
              <InfoCircleOutlined />
              <span>{t('verification.unavailable_now', { reason: verified.unavailable_reason })}</span>
            </div>
          )}
        </>
      );
    }

    if (failure) {
      return (
        <Alert
          className="mt-sm"
          type="error"
          showIcon
          icon={<CloseCircleFilled />}
          title={failure.message ?? t('verification.failed')}
          description={failure.reason ? t('verification.wolt_reason', { reason: failure.reason }) : undefined}
        />
      );
    }

    return null;
  };

  const renderActivation = () =>
    verified && (
      <div className="mt-md">
        <div className="font-medium mb-xs">{t('fields.priority.label')}</div>
        <Radio.Group
          value={priority}
          onChange={(event) => setPriority(event.target.value)}
          options={Object.values(WoltDriveProviderPriority).map((value) => ({
            value,
            label: t(`fields.priority.options.${value}`),
          }))}
          vertical
        />
        <div className="mt-sm text-sm">
          <span className="text-gray-500">{t('preview.label')}: </span>
          <span>{formatDeliveryProviderChain(verified.current_delivery_providers, t('preview.none'))}</span>
          <span className="mx-xs">→</span>
          <span className="font-medium">{formatDeliveryProviderChain(plannedProviders)}</span>
        </div>
        {company?.delivery_mode !== DeliveryMode.External && (
          <Alert className="mt-sm" type="info" showIcon title={t('hints.internal_delivery')} />
        )}
        <Popconfirm
          title={t('confirm.title')}
          description={
            <>
              <div>
                {t('confirm.description', {
                  venueId: maskWoltVenueId(verified.venueId),
                  providers: formatDeliveryProviderChain(plannedProviders),
                })}
              </div>
              {hasUnsavedProviderChanges && <div className="mt-xs">{t('confirm.unsaved_providers')}</div>}
            </>
          }
          okText={t('confirm.ok')}
          cancelText={t('confirm.cancel')}
          okButtonProps={{ loading: saving }}
          onConfirm={save}
        >
          <Button type="primary" className="mt-md" loading={saving}>
            {t('buttons.save')}
          </Button>
        </Popconfirm>
      </div>
    );

  return (
    <Card className="mb-md">
      <div className="text-lg font-medium">{t('title')}</div>
      <div className="text-sm text-gray-500 mb-md">{t('subtitle')}</div>

      <div className="mb-lg">
        <div className="font-medium mb-xs">{t('steps.request.title')}</div>
        <div className="text-sm text-gray-500 mb-sm">{t('steps.request.description')}</div>
        <Button icon={<CopyOutlined />} loading={copying} onClick={copyDetails}>
          {t('buttons.copy_details')}
        </Button>
      </div>

      <div>
        <div className="font-medium mb-xs">{t('steps.activate.title')}</div>
        <div className="text-sm text-gray-500 mb-sm">{t('steps.activate.description')}</div>
        {/* The card only shows without a valid venue ID, so one stored here is malformed. */}
        {company?.wolt_drive_venue_id && (
          <Alert
            className="mb-sm"
            type="warning"
            showIcon
            title={t('hints.invalid_stored_id', { venueId: company.wolt_drive_venue_id })}
          />
        )}
        <label htmlFor="wolt-drive-venue-id" className="block mb-xs">
          {t('fields.venue_id.label')}
        </label>
        <div className="flex gap-2">
          <Input
            id="wolt-drive-venue-id"
            className="max-w-[448px] font-mono"
            value={venueIdInput}
            placeholder={t('fields.venue_id.placeholder')}
            status={showFormatError ? 'error' : undefined}
            onChange={(event) => setVenueIdInput(event.target.value)}
            onPressEnter={() => candidateIsValid && !verifying && verify()}
            autoComplete="off"
            spellCheck={false}
          />
          <Button onClick={verify} loading={verifying} disabled={!candidateIsValid}>
            {t('buttons.verify')}
          </Button>
        </div>
        {showFormatError && <div className="text-sm text-red-500 mt-xs">{t('fields.venue_id.invalid')}</div>}
        {renderVerificationResult()}
        {renderActivation()}
      </div>
    </Card>
  );
}
